/**
 * stories.js — Phase "Stories Hub" canonical registry.
 *
 * VOCABULARY (fixed): a SERIES contains ordered EPISODES; an EPISODE
 * contains ordered PAGES. Age bands are "seedlings" | "explorers" |
 * "pioneers" (matching /academy).
 *
 * Content is migrated from the existing data/storyArcs.js narrative
 * wrappers. We DO NOT rewrite prose and DO NOT fabricate level variants:
 * every episode's existing text is placed under the single band its
 * vocabulary actually sits in (all four current series read at the
 * "explorers" / 7-10 level). resolveLevel() serves the nearest available
 * band and reports whether it was an exact match.
 *
 * Offline-first: this registry is bundled with the app, so the hub and
 * reader work with no network once the app shell is cached by the SW.
 */
import { STORY_ARCS } from "@/data/storyArcs";
import { STORY_BEATS } from "@/data/storyBeats";

const BANDS = ["seedlings", "explorers", "pioneers"];
export const BAND_LABEL = { seedlings: "Seedlings", explorers: "Explorers", pioneers: "Pioneers" };
export const BAND_AGES = { seedlings: "4–6", explorers: "7–10", pioneers: "11–15" };

// url-safe slug from a title.
function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const THEME_HEX = {
  "bg-sky-200": "#38bdf8",
  "bg-amber-200": "#f59e0b",
  "bg-rose-200": "#f43f5e",
  "bg-violet-200": "#8b5cf6",
  "bg-emerald-200": "#10b981",
};

// Illustrated covers live in /generated/stories (WebP, 960×640 + 480×320 thumb).
const COVER = (name) => ({ coverImage: `/generated/stories/${name}.webp`, coverThumb: `/generated/stories/${name}-sm.webp` });

// Per-series band assignment (A3) + tags. Justification kept in comments.
const SERIES_META = {
  "lost-explorer": { slug: "the-lost-explorer", band: "explorers", tags: ["adventure", "geography"], blurb: "Follow five clues a traveller left across the continents.", countries: ["br", "ke", "jp", "in", "eg", "fr"], ...COVER("lost-explorer"), themeColor: "#0e7490" },
  "number-island": { slug: "number-island",     band: "explorers", tags: ["adventure", "maths"],     blurb: "An island reveals itself only to children who solve its five puzzles.", countries: ["jp", "in"], ...COVER("number-island"), themeColor: "#ea580c" },
  "word-thief":    { slug: "the-word-thief",    band: "explorers", tags: ["adventure", "english"],   blurb: "Someone is stealing words from the town's storybook — track them down.", countries: ["fr", "eg"], ...COVER("word-thief"), themeColor: "#be185d" },
  "moonlit-map":   { slug: "the-moonlit-map",   band: "explorers", tags: ["adventure", "mixed"],     blurb: "A silver map appears each night; every subject leaves a mark on it.", countries: ["br", "ke", "jp", "fr"], ...COVER("moonlit-map"), themeColor: "#4338ca" },
  "cartographer-cipher": { slug: "the-cartographers-cipher", band: "pioneers", tags: ["mystery", "mixed"], blurb: "Six chapters, every subject: decode the message a stranger hid in the world's data.", countries: ["ke", "eg"], ...COVER("cipher"), themeColor: "#0f766e" },
  "rainforest-radio": { slug: "the-rainforest-radio", band: "explorers", tags: ["adventure", "science"], blurb: "An old radio picks up voices from the Amazon — five signals, one rescue.", countries: ["br"], ...COVER("rainforest-radio"), themeColor: "#15803d" },
  "pip-puffin":    { slug: "pip-the-puffin",     band: "seedlings", tags: ["gentle", "feelings"],    blurb: "A small puffin, a big blue world, and five little steps to brave.", countries: ["is"], ...COVER("pip-puffin"), themeColor: "#0284c7" },
};

function pagesFromEpisode(ep, arcId) {
  const pages = [];
  if (ep.narrativeIntro) pages.push({ text: ep.narrativeIntro, image: null, alt: null });
  const beats = ep.beats || STORY_BEATS[`${arcId}:${ep.day}`] || [];
  for (const b of beats) pages.push({ text: b, image: null, alt: null });
  if (ep.cliffhanger) pages.push({ text: ep.cliffhanger, image: null, alt: null });
  if (!pages.length) pages.push({ text: ep.title || "Story time", image: null, alt: null });
  return pages;
}

function wordCount(pages) {
  return pages.reduce((n, p) => n + String(p.text || "").trim().split(/\s+/).filter(Boolean).length, 0);
}

// Build the canonical SERIES array once at module load.
export const SERIES = STORY_ARCS.map((arc) => {
  const meta = SERIES_META[arc.id] || { slug: slugify(arc.id), band: "explorers", tags: ["adventure"], blurb: arc.hookLine || "" };
  const band = meta.band;
  const episodes = (arc.episodes || []).map((ep, i) => {
    const pages = pagesFromEpisode(ep, arc.id);
    const words = wordCount(pages);
    return {
      slug: slugify(ep.title) || `episode-${i + 1}`,
      seriesSlug: meta.slug,
      index: i + 1,
      title: ep.title || `Episode ${i + 1}`,
      coverImage: null,
      audioUrl: null,
      status: "published",
      relatedQuest: ep.activityId ? { to: `${ep.activityId}${ep.activityQuery || ""}`, label: ep.activityLabel || "Try the linked quest" } : null,
      levels: {
        [band]: {
          pages,
          wordCount: words,
          readingMinutes: Math.max(1, Math.round(words / 90)),
        },
      },
    };
  });
  return {
    slug: meta.slug,
    title: arc.title,
    blurb: (meta.blurb || arc.hookLine || "").slice(0, 160),
    coverImage: meta.coverImage || null,
    coverThumb: meta.coverThumb || meta.coverImage || null,
    themeColor: meta.themeColor || THEME_HEX[arc.color] || "#38bdf8",
    emoji: arc.emoji || "📖",
    bands: [band],
    tags: meta.tags,
    countries: meta.countries || [],
    episodeCount: episodes.length,
    status: "published",
    episodes,
  };
});

const bySlug = Object.fromEntries(SERIES.map((s) => [s.slug, s]));

// ── Read-only helpers ───────────────────────────────────────────────

/** Nearest-band resolver (A4). Never index levels directly. */
export function resolveLevel(episode, requestedBand) {
  if (!episode || !episode.levels) return { level: null, bandServed: null, isExactLevel: false };
  if (episode.levels[requestedBand]) {
    return { level: episode.levels[requestedBand], bandServed: requestedBand, isExactLevel: true };
  }
  // Nearest available band by BANDS order distance.
  const available = BANDS.filter((b) => episode.levels[b]);
  if (!available.length) return { level: null, bandServed: null, isExactLevel: false };
  const reqIdx = BANDS.indexOf(requestedBand);
  let best = available[0];
  let bestDist = Infinity;
  for (const b of available) {
    const d = Math.abs(BANDS.indexOf(b) - (reqIdx === -1 ? 1 : reqIdx));
    if (d < bestDist) { bestDist = d; best = b; }
  }
  return { level: episode.levels[best], bandServed: best, isExactLevel: false };
}

export function getSeries(seriesSlug) {
  return bySlug[seriesSlug] || null;
}

export function getEpisode(seriesSlug, episodeSlug) {
  const s = getSeries(seriesSlug);
  if (!s) return null;
  return s.episodes.find((e) => e.slug === episodeSlug) || null;
}

export function episodeNeighbours(seriesSlug, episodeSlug) {
  const s = getSeries(seriesSlug);
  if (!s) return { prev: null, next: null };
  const idx = s.episodes.findIndex((e) => e.slug === episodeSlug);
  return {
    prev: idx > 0 ? s.episodes[idx - 1] : null,
    next: idx >= 0 && idx < s.episodes.length - 1 ? s.episodes[idx + 1] : null,
  };
}

/** Total reading minutes for a series at the given (resolved) band. */
export function seriesReadingMinutes(series, band = "explorers") {
  return series.episodes.reduce((n, ep) => {
    const { level } = resolveLevel(ep, band);
    return n + (level?.readingMinutes || 0);
  }, 0);
}

/** All unique tags across published series. */
export function allTags() {
  const set = new Set();
  for (const s of SERIES) for (const t of s.tags) set.add(t);
  return [...set];
}

/** Hub listing with band/search/tag filters (mirrors GET /api/stories). */
export function listSeries({ band = null, q = "", tag = null } = {}) {
  let out = SERIES.filter((s) => s.status === "published");
  if (band && band !== "all") out = out.filter((s) => s.bands.includes(band));
  if (tag && tag !== "all") out = out.filter((s) => s.tags.includes(tag));
  if (q) {
    const needle = q.toLowerCase();
    out = out.filter(
      (s) =>
        s.title.toLowerCase().includes(needle) ||
        s.blurb.toLowerCase().includes(needle) ||
        s.tags.some((t) => t.includes(needle))
    );
  }
  return out;
}

export const STORY_TOTALS = {
  series: SERIES.length,
  episodes: SERIES.reduce((n, s) => n + s.episodeCount, 0),
};

/** Map a legacy storyArcs arc id → canonical series slug. */
export function seriesSlugForArc(arcId) {
  return (SERIES_META[arcId] && SERIES_META[arcId].slug) || null;
}
